import { useMemo, useRef, useState } from 'react';
import { IconButton, InputAdornment, Slider, Stack, TextField } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { ArrowRight, X } from 'lucide-react';

/**
 * Numeric range picker: histogram bars + dual-handle slider + min/max inputs
 * (each clearable). The selected range is marked by a light shaded band over the
 * histogram between two handles (Figma "sliderzone" / "slider-handle": a hairline
 * the height of the chart with a grip at its middle, in a deeper ink than the
 * bars, so a handle never reads as one more bar); bars inside the range render in
 * the accent color, the rest muted. Null bounds mean "open" on that side.
 *
 * `onChange` fires ONCE PER GESTURE, never per pixel or keystroke: when a handle
 * is released (or its arrow key is), on Enter or blur in a field, and on a field's
 * ✕. While a handle moves, the band, the bars and the fields follow it locally.
 * The handles never cross.
 *
 * Generic design-system component: colors default to the MUI theme; accent and
 * handle overrides exist for feature palettes, and the caller may supply the
 * number format the fields show and read.
 */

const BARS_HEIGHT = 60;
/** A handle's grab area; only the hairline and the grip inside it are drawn. */
const HANDLE_HIT_WIDTH = 12;
/** Figma `slider-handle`: a 3 × 22 grip, radius 2, on a 1px line. */
const GRIP_WIDTH = 3;
const GRIP_HEIGHT = 22;
/** A dragged bound lands on whole hundredths, so no drag hands back float noise. */
const DRAG_DECIMALS = 2;

type Bounds = [number | null, number | null];
type Side = 0 | 1;
/** One handle under way: which, and where it is. */
type Gesture = { side: Side; at: number };

const BarsWrap = styled('div')({
  position: 'relative',
  height: BARS_HEIGHT,
});

/** Light shaded band marking the selected range, behind the bars. */
const Band = styled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  zIndex: 0,
  pointerEvents: 'none',
});

const BarsRow = styled('div')({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'flex-end',
  gap: 1,
  height: '100%',
});

const Bar = styled('div')({
  flex: 1,
  minWidth: 2,
  borderRadius: 1,
});

const roundTo = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const parseLenient = (text: string): number | null => {
  const n = Number(text.replace(/[’'\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

export interface RangeHistogramSliderProps {
  /** Full data extent. */
  min: number;
  max: number;
  /** Row count per bucket, left-to-right across [min, max]. */
  histogram: number[];
  /** Selected bounds; null = unbounded on that side. */
  value: Bounds;
  /** The new bounds, once per gesture (see the component note). */
  onChange: (value: Bounds) => void;
  /** Accent for in-range bars and the band (defaults to theme primary). */
  accentColor?: string;
  /** Ink of the two handles (defaults to the theme's dark primary). */
  handleColor?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  /** How a field shows a bound (defaults to `String`). */
  formatValue?: (value: number) => string;
  /** Reads a typed bound; `null` refuses the text and the field reverts. */
  parseValue?: (text: string) => number | null;
  disabled?: boolean;
}

export function RangeHistogramSlider({
  min,
  max,
  histogram,
  value,
  onChange,
  accentColor,
  handleColor,
  minPlaceholder,
  maxPlaceholder,
  formatValue = String,
  parseValue = parseLenient,
  disabled,
}: RangeHistogramSliderProps) {
  const theme = useTheme();
  const accent = accentColor ?? theme.palette.primary.main;
  const handle = handleColor ?? theme.palette.primary.dark;
  const muted = theme.palette.grey[300] ?? alpha(theme.palette.text.disabled, 0.4);

  /** The handle mid-gesture, for the band, the bars and the fields to follow. */
  const [drag, setDrag] = useState<Gesture | null>(null);
  /**
   * The same gesture, readable inside the event that ends it — MUI reports a key's
   * change and its commit in one handler, before any re-render. Null when nothing
   * moved: MUI commits its LAST change even for a gesture that made none (a click on a
   * handle), and after a typed bound that would be a stale position.
   */
  const gesture = useRef<Gesture | null>(null);
  /** A field's text while it is being edited; null shows the bound. */
  const [typed, setTyped] = useState<[string | null, string | null]>([null, null]);

  const [lo, hi] = value;
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  // What the slider is handed — a bound typed past the data's extent draws at its end.
  const sliderValue = [clamp(lo ?? min), clamp(hi ?? max)];

  /** A dragged handle's bound: unset at the data's end, else on whole hundredths. */
  const toBound = (v: number, side: Side): number | null => {
    const rounded = roundTo(v, DRAG_DECIMALS);
    const atEnd =
      side === 0 ? rounded <= roundTo(min, DRAG_DECIMALS) : rounded >= roundTo(max, DRAG_DECIMALS);
    return atEnd ? null : rounded;
  };

  /**
   * The bounds with one side replaced. It stops at the other bound rather than
   * crossing it — typed past it, or nudged past it by the rounding of a handle
   * that stopped there.
   */
  const withBound = (side: Side, bound: number | null): Bounds => {
    const other = value[side === 0 ? 1 : 0];
    const kept =
      bound === null || other === null
        ? bound
        : side === 0
          ? Math.min(bound, other)
          : Math.max(bound, other);
    return side === 0 ? [kept, hi] : [lo, kept];
  };

  /**
   * Only the handle under way is read from the slider. The other keeps its bound —
   * one typed past the data's extent, which the slider can only draw at its end, or
   * one typed a moment ago and committed by the blur that grabbing this handle caused.
   */
  const fromGesture = ({ side, at }: Gesture): Bounds => withBound(side, toBound(at, side));

  const shown: Bounds = drag ? fromGesture(drag) : value;
  const effLo = shown[0] ?? min;
  const effHi = shown[1] ?? max;
  const maxCount = useMemo(() => Math.max(1, ...histogram), [histogram]);
  const span = max - min || 1;

  // Selected-range band position, as % of the full extent.
  const loPct = Math.max(0, Math.min(100, ((effLo - min) / span) * 100));
  const hiPct = Math.max(0, Math.min(100, ((effHi - min) / span) * 100));

  const publish = (next: Bounds) => {
    if (next[0] !== lo || next[1] !== hi) onChange(next);
  };

  /** Ends the gesture under way, if one moved anything. */
  const commitGesture = () => {
    const ended = gesture.current;
    if (!ended) return;
    gesture.current = null;
    setDrag(null);
    publish(fromGesture(ended));
  };

  const setTypedSide = (side: Side, text: string | null) =>
    setTyped((prev) => (side === 0 ? [text, prev[1]] : [prev[0], text]));

  const commitTyped = (side: Side) => {
    const text = typed[side];
    if (text === null) return;
    setTypedSide(side, null);
    const blank = text.trim() === '';
    const parsed = blank ? null : parseValue(text);
    // Unreadable text is not a bound: the field reverts to the one it had.
    if (!blank && parsed === null) return;
    publish(withBound(side, parsed));
  };

  const field = (side: Side) => {
    const bound = shown[side];
    const name = side === 0 ? 'min' : 'max';
    return (
      <TextField
        size='small'
        fullWidth
        disabled={disabled}
        placeholder={side === 0 ? minPlaceholder : maxPlaceholder}
        value={typed[side] ?? (bound === null ? '' : formatValue(bound))}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setTypedSide(side, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitTyped(side);
        }}
        onBlur={() => commitTyped(side)}
        inputProps={{ 'data-testid': `range-${name}-input`, inputMode: 'decimal' }}
        InputProps={{
          endAdornment: bound !== null && (
            <InputAdornment position='end'>
              {/* Pointer-only, as in the prototype: Tab goes min → max. Pressing it
                  must not blur the field first, or its text would be written
                  before the ✕ clears it — two writes for one click. */}
              <IconButton
                size='small'
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setTypedSide(side, null);
                  publish(withBound(side, null));
                }}
                aria-label={`clear ${name}`}
                data-testid={`range-${name}-clear`}
              >
                <X size={12} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  };

  return (
    <Stack spacing={1} data-testid='range-histogram-slider'>
      <BarsWrap>
        <Band
          aria-hidden
          style={{
            left: `${loPct}%`,
            width: `${Math.max(0, hiPct - loPct)}%`,
            backgroundColor: alpha(accent, 0.14),
          }}
        />
        <BarsRow aria-hidden>
          {histogram.map((count, i) => {
            const center = min + ((i + 0.5) / histogram.length) * span;
            const inRange = center >= effLo && center <= effHi;
            return (
              <Bar
                key={i}
                style={{
                  height: `${Math.max(4, (count / maxCount) * 100)}%`,
                  backgroundColor: inRange ? accent : muted,
                  opacity: count === 0 ? 0.35 : 1,
                }}
              />
            );
          })}
        </BarsRow>
        <Slider
          disabled={disabled}
          value={
            drag
              ? drag.side === 0
                ? [drag.at, sliderValue[1]]
                : [sliderValue[0], drag.at]
              : sliderValue
          }
          min={min}
          max={max}
          step={span / 100}
          disableSwap
          onChange={(_e, v, activeThumb) => {
            const next: Gesture = { side: activeThumb as Side, at: (v as number[])[activeThumb] };
            gesture.current = next;
            setDrag(next);
          }}
          // A held arrow key commits on every repeat; its run is one gesture, written
          // when the key comes up (or focus leaves). A pointer writes on release.
          onChangeCommitted={(e) => {
            if (e.type !== 'keydown') commitGesture();
          }}
          onKeyUp={commitGesture}
          onBlur={commitGesture}
          getAriaValueText={(v, index) =>
            formatValue(shown[index as Side] ?? roundTo(v, DRAG_DECIMALS))
          }
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            padding: 0,
            zIndex: 2,
            // The handles draw in `currentColor`, so a disabled slider greys them.
            color: handle,
            // The band above draws the fill; the slider only carries the handles.
            '& .MuiSlider-rail, & .MuiSlider-track': { opacity: 0, border: 'none' },
            '& .MuiSlider-thumb': {
              width: HANDLE_HIT_WIDTH,
              height: BARS_HEIGHT,
              borderRadius: 0,
              backgroundColor: 'transparent',
              boxShadow: 'none',
              cursor: 'grab',
              // The hairline, the full height of the chart…
              '&::before': {
                top: 0,
                left: '50%',
                // Strings, not numbers: `sx` reads `width: 1` as 100%.
                width: '1px',
                height: '100%',
                transform: 'translateX(-50%)',
                borderRadius: 0,
                backgroundColor: 'currentColor',
                boxShadow: 'none',
              },
              // …and the grip at its middle (MUI's invisible touch target, redrawn).
              '&::after': {
                width: GRIP_WIDTH,
                height: GRIP_HEIGHT,
                // A string again: `sx` reads `borderRadius: 2` as twice the theme radius.
                borderRadius: '2px',
                backgroundColor: 'currentColor',
              },
              '&:hover, &.Mui-focusVisible, &.Mui-active': { boxShadow: 'none' },
              '&:hover::after, &.Mui-focusVisible::after, &.Mui-active::after': {
                boxShadow: `0 0 0 4px ${alpha(handle, 0.2)}`,
              },
              '&.Mui-active': { cursor: 'grabbing' },
            },
          }}
          data-testid='range-slider'
        />
      </BarsWrap>

      <Stack direction='row' spacing={1} alignItems='center'>
        {field(0)}
        <ArrowRight size={16} style={{ flexShrink: 0 }} aria-hidden />
        {field(1)}
      </Stack>
    </Stack>
  );
}

export default RangeHistogramSlider;
